import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { useHandleAuthCallback, useLogin, useNotify, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { stringify } from 'safe-stable-stringify';

import { ZodPostRequestEmailRequest } from '@Common/types/bo_external/auth/request_email';

import { BrandAwareLogo, ExternalLink, TypographyTranslated } from '@sh4res/bo-common/components';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';
import { PasswordInput } from '@sh4res/bo-common/fields';
import { getTheme } from '@sh4res/bo-common/theme';

import { AddAlternateEmailPageForm } from '@Components/AddAlternateEmailPageForm';
import { AxaTypography } from '@Components/AxaTypography';
import { LoginPageCTA, LoginPageCTASecondary } from '@Components/LoginPageCTA';
import { VerifyOtpPageForm } from '@Components/VerifyOtpPageForm';
import { TranslationKeys } from '@I18n/types';
import { authClient, getAuthSession } from '@Utils/authClient';
import { getTwoFactorOtpConfig } from '@Utils/loginTwoFactor';
import { STORAGE_KEYS } from '@Utils/storage_keys';
import { TwoFactorRequired } from '@Utils/twoFactorRequired';

const LoginPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
`;

function EmailPasswordForm({
  onTwoFactor,
  onGoBack,
}: {
  onTwoFactor: (state: TwoFactorRequired) => void;
  onGoBack: () => void;
}) {
  const translate = useTranslate();
  const login = useLogin();
  const notify = useNotify();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({ mode: 'onBlur', resolver: zodResolver(ZodPostRequestEmailRequest) });
  const auth = useHandleAuthCallback({ enabled: false });

  const [loading, setLoading] = useState(false);
  const testId = 'BoExternalEmailPasswordForm';

  const onSubmit = async (formData: unknown) => {
    setLoading(true);
    try {
      await login(formData);
      // reload identity
      await auth.refetch();
      // set a flag to check for init tour on the next page load
      localStorage.setItem(STORAGE_KEYS.loginSuccessTourCheck, 'true');
    } catch (error) {
      setLoading(false);
      if (error instanceof TwoFactorRequired) {
        onTwoFactor(error);
        return;
      }

      // oxlint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error(error);
      notify('login_page.messages.form.errors.invalid_credentials', { type: 'error' });
    }
  };

  return (
    <>
      <PageTitle title={translate('login_page.messages.login.header')} />
      <IconButton
        data-testid={`${testId}_BackButton`}
        onClick={onGoBack}
        aria-label={translate('contact_support_page.accessibility.back')}
        sx={{
          color: 'black',
          backgroundColor: 'transparent !important;',
          position: 'absolute',
          top: 12,
          left: 12,
        }}>
        <ChevronLeftIcon sx={{ fontSize: '23px' }} />
      </IconButton>
      <LoginPageFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <TypographyTranslated
          label="login_page.messages.login.header"
          variant="h5"
          component={AxaTypography}
          textAlign="left"
          sx={{ mt: '40px' }}
        />

        <TypographyTranslated
          label="login_page.messages.login.subheader"
          variant="body1"
          textAlign="left"
          sx={{ mb: '16px' }}
        />
        <TextField
          data-testid={`${testId}_EmailInput`}
          label={translate('login_page.messages.form.email_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}
          defaultValue=""
          error={!!errors.email}
          helperText={
            G.isNotNullable(errors.email?.message)
              ? translate(errors.email.message as TranslationKeys)
              : undefined
          }
          {...register('email')}
        />
        <PasswordInput
          testId={`${testId}_PasswordInput`}
          label={translate('login_page.messages.form.password_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            width: '100%',
          }}
          defaultValue=""
          error={!!errors.password}
          helperText={
            G.isNotNullable(errors.password?.message)
              ? translate(errors.password.message as TranslationKeys)
              : undefined
          }
          {...register('password')}
        />
        <Stack direction="row">
          <Link
            style={{
              textAlign: 'left',
            }}
            component={RouterLink}
            to="/forgot_password">
            {translate('login_page.messages.form.forgot_password')}
          </Link>
        </Stack>

        <Stack direction="row" mt={4} alignItems="center">
          <FormControl
            variant="outlined"
            sx={{
              borderRadius: '16px',
              width: '100%',
              margin: 0,
            }}>
            <FormControlLabel
              label={translate('login_page.messages.form.remember_me')}
              control={<Checkbox data-testid={`${testId}_RememberMeCheckbox`} />}
              {...register('rememberMe')}
            />
          </FormControl>
          <div>
            <LoginPageCTA
              data-testid={`${testId}_SubmitButton`}
              type="submit"
              variant="contained"
              sx={{ textWrap: 'nowrap' }}
              disabled={!isValid || loading}>
              <TypographyTranslated
                component={AxaTypography}
                label="login_page.messages.form.cta_text"
              />
            </LoginPageCTA>
          </div>
        </Stack>
      </LoginPageFormWrapper>
    </>
  );
}

function LandingPage({
  onLogin,
  onFirstConnection,
}: {
  onLogin: () => void;
  onFirstConnection: () => void;
}) {
  const translate = useTranslate();
  const testId = 'BoExternalLandingPage';

  const title = translate(
    `login_page.messages.landing.${getTheme() === 'mlk' ? 'mlk_' : ''}header.line1`,
  );
  const subtitle = translate(
    `login_page.messages.landing.${getTheme() === 'mlk' ? 'mlk_' : ''}header.line2`,
  );

  return (
    <>
      <PageTitle title={subtitle} />
      <Box marginTop={8}>
        <BrandAwareLogo height="80" />
      </Box>
      <Box position="absolute" top={24} right={24}>
        <CustomLocalesMenuButton testId={`${testId}_LocalesMenuButton`} />
      </Box>

      <AxaTypography
        variant="h5"
        sx={{
          mt: '40px',
        }}>
        <Box textAlign="center">{title}</Box>
        <Box textAlign="center">{subtitle}</Box>
      </AxaTypography>

      <Box flex={1} minHeight="200px"></Box>
      <Box display="flex" gap={2} width="100%">
        <LoginPageCTASecondary onClick={onFirstConnection} variant="contained">
          <TypographyTranslated
            component={AxaTypography}
            label="login_page.messages.landing.first_connection"
          />
        </LoginPageCTASecondary>

        <LoginPageCTA
          data-testid={`${testId}_LoginButton`}
          onClick={onLogin}
          variant="contained"
          sx={{
            background: '#00008F',
            marginRight: '0 !important',
            fontSize: 16,
          }}>
          <TypographyTranslated
            component={AxaTypography}
            label="login_page.messages.landing.login"
          />
        </LoginPageCTA>
      </Box>
      <Typography sx={{ marginTop: 4 }}>
        {translate('login_page.messages.landing.footer')}
        <ExternalLink
          variant="body1"
          sx={{ color: 'primary.main' }}
          href={'https://www.axa.com/'}
          referrerPolicy="no-referrer"
          rel="noreferrer">
          {translate('login_page.messages.landing.footer_link')}
        </ExternalLink>
      </Typography>
    </>
  );
}

export function LoginPageForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { nextPathname: string; nextSearch: string } | undefined;
  const nextPathName = locationState?.nextPathname;
  const nextSearch = locationState?.nextSearch;

  const [twoFactor, setTwoFactor] = useState<TwoFactorRequired | null>(null);

  const [isLandingPage, setIsLandingPage] = useState(true);

  const disableLandingPage = () => setIsLandingPage(false);
  const enableLandingPage = () => setIsLandingPage(true);

  const handleGoToFirstConnection = () => {
    navigate('/first_connection');
  };

  if (isLandingPage) {
    return (
      <LandingPage onLogin={disableLandingPage} onFirstConnection={handleGoToFirstConnection} />
    );
  }

  return twoFactor === null ? (
    <EmailPasswordForm onTwoFactor={setTwoFactor} onGoBack={enableLandingPage} />
  ) : (
    (() => {
      const otpConfig = getTwoFactorOtpConfig(twoFactor.otpChannel);

      if (otpConfig.kind === 'contact_support') {
        return (
          <AddAlternateEmailPageForm
            onBackToLogin={async () => {
              await authClient.signOut();
              setTwoFactor(null);
            }}
          />
        );
      }

      return (
        <VerifyOtpPageForm
          showLanguagePicker={false}
          autoSend
          type={otpConfig.type}
          alternateEmail={otpConfig.alternateEmail}
          send={async () => authClient.twoFactor.sendOtp()}
          verify={async (code, fetchOptions) =>
            authClient.twoFactor.verifyOtp(
              { code, trustDevice: twoFactor.trustDevice },
              fetchOptions,
            )
          }
          onSuccess={async (phoneNumberVerified) => {
            localStorage.setItem(STORAGE_KEYS.loginSuccessTourCheck, 'true');

            await setDefaultAdminActive();

            const { data: session } = await authClient.getSession();
            const hasAlternateEmail = !!session?.user.alternateEmail;

            if (phoneNumberVerified || hasAlternateEmail) {
              navigate(`${nextPathName ?? ''}${nextSearch ?? ''}` || '/', { replace: true });
            } else {
              navigate('/verify_phone_number', {
                replace: true,
                state: {
                  nextPathname: nextPathName,
                  nextSearch: nextSearch ?? '',
                },
              });
            }
          }}
          onExpired={setTwoFactor}
        />
      );
    })()
  );
}

export async function setDefaultAdminActive() {
  const { data: session } = await getAuthSession();
  if (session?.user.role !== 'b2b_admin') {
    return;
  }

  const activeAdminId = localStorage.getItem(STORAGE_KEYS.activeAdminId);

  if (activeAdminId === null) {
    const admins = await authClient.users.list();

    if (G.isNullable(admins.data) || admins.data.users.length === 0) {
      throw new Error('Failed to retrieve admin');
    }
    const adminId = admins.data.users[0].id;

    await authClient.users.active({ userId: adminId });

    localStorage.setItem(STORAGE_KEYS.activeAdminId, String(adminId));
    localStorage.setItem(STORAGE_KEYS.adminsList, stringify(admins.data.users));
  }
}
